import { arrayBufferToString, stringToArrayBuffer, subtle } from './'
import {
  encode,
  decode,
  generatePrivateMembers as privateProps
} from '../utils'

import { wrap } from '../../../utils'
import { createStorage } from '../../../utils/storage'
import { SPACE } from '../../../constants'
import { CRYPTOSTORAGE_SECRET_KEY } from '../constants'

interface PrivateProps {
  secret: symbol,
  key: symbol,
  token: symbol,
  transaction: symbol,
  storage: symbol
}

const props: PrivateProps = privateProps(['secret', 'key', 'token', 'transaction', 'storage'])
const keyPrefix = 'nugsnet:sdk:crypto'

export const enum CRYPT_TYPE {
  AES_CTR = 'AES-CTR',
  AES_CBC = 'AES-CBC',
  AES_GCM = 'AES-GCM',
  RSA_OAEP = 'RSA-OAEP'
}

export const enum HASH_TYPE {
  SHA1 = 'SHA-1',
  SHA256 = 'SHA-256',
  SHA384 = 'SHA-384',
  SHA512 = 'SHA-512'
}

type WebCryptoAlgorithm = {
  name: CRYPT_TYPE,
  length: number
}

export class WebCryptoError extends Error {
  name = 'WebCryptoError'
  message = 'Missing encryption key or webcrypto is not supported.'
}

export class WebCryptoTransactionError extends Error {
  name = 'WebCryptoTransactionError'
  message = 'Storage is locked. Unable to write a data. Please finsih the last trasaction first.'
}

export class WebCryptoStorageError extends Error {
  name = 'WebCryptoStorageError'

  constructor(message = SPACE) {
    super(`Storage error. ${message}`.trim())
  }
}

export class WebCryptoStorage {
  #isConfigured = false

  private static get usages(): KeyUsage[] {
    return ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
  }

  private static get algorithm(): WebCryptoAlgorithm {
    return { name: CRYPT_TYPE.AES_GCM, length: 256 }
  }

  private static get SDK_CRYPTO_STORAGE_VERSION_KEY(): string {
    return keyPrefix + ':version'
  }

  private static get SDK_CRYPTO_STORAGE_KEY(): string {
    return keyPrefix + ':data'
  }

  private static get SDK_CRYPTO_STORAGE_PRIVATE_KEY(): string {
    return keyPrefix + ':key'
  }

  private get storage(): Storage {
    return this[props.storage as never]
  }

  private get transaction(): Promise<void> {
    return this[props.transaction as never]
  }

  public get isEmpty(): boolean {
    return !this.storage.getItem(WebCryptoStorage.SDK_CRYPTO_STORAGE_KEY)
  }

  public get pKey() {
    const pkey = (<Storage> this[props.storage as never]).getItem(WebCryptoStorage.SDK_CRYPTO_STORAGE_PRIVATE_KEY)
    if (pkey) return stringToArrayBuffer(window.atob(pkey))
    return pkey
  }

  constructor(secret: string, version: number, permanent = false) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const self = this as any
    self[props.secret] = encode(secret)
    self[props.transaction] = null
    // Kill session on browser close.
    self[props.storage] = createStorage({ permanent })
    this.upgradeStorageVersion(version)
  }

  private static pKey(algorithm: WebCryptoAlgorithm, pkey: ArrayBuffer) {
    try {
      if (pkey && subtle && subtle.importKey) {
        return subtle.importKey('raw', pkey, algorithm, true, WebCryptoStorage.usages)
      } else if (subtle && subtle.generateKey) {
        return subtle.generateKey(
          algorithm,
          true,
          WebCryptoStorage.usages
        )
      }

      return null
    } catch (error) {
      return null
    }
  }

  private upgradeStorageVersion(version: number): void {
    try {
      const key = WebCryptoStorage.SDK_CRYPTO_STORAGE_VERSION_KEY
      const currentVersion = Number(this.storage.getItem(key) || 0)

      if (currentVersion < version) {
        this.storage.removeItem(key)
        this.storage.removeItem(WebCryptoStorage.SDK_CRYPTO_STORAGE_KEY)

        this.storage.setItem(key, String(version))
      }
    } catch {
      console.warn('Storage version upgrade failed')
    }
  }

  private async savePrivateKey() {
    if (!this[props.key as never]) return

    try {
      if (subtle && subtle.exportKey) {
        const pKey = await subtle.exportKey('raw', this[props.key as never]);
        (<Storage> this[props.storage as never]).setItem(WebCryptoStorage.SDK_CRYPTO_STORAGE_PRIVATE_KEY, window.btoa(arrayBufferToString(pKey)))
      }
    } catch {
      this.storage.removeItem(WebCryptoStorage.SDK_CRYPTO_STORAGE_PRIVATE_KEY)
    }
  }

  public async setup(algorithm = WebCryptoStorage.algorithm): Promise<void> {
    if (this.#isConfigured || !this.pKey) return
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const self = this as any
      self[props.key] = await WebCryptoStorage.pKey(algorithm, this.pKey as ArrayBuffer)

      await this.savePrivateKey()
    } catch {
      this.storage.removeItem(WebCryptoStorage.SDK_CRYPTO_STORAGE_PRIVATE_KEY)
    }
  }

  public async encrypt(string: string): Promise<string> {
    const encoded: ArrayBuffer = encode(string)

    const key = this[props.key as never]
    const encryptBy = { name: CRYPT_TYPE.AES_GCM, iv: this[props.secret as never] }

    try {
      if (key && subtle) {
        const buffer: ArrayBuffer = await subtle.encrypt(
          encryptBy,
          key,
          encoded
        )

        return window.btoa(Array.from(new Uint8Array(buffer)).join(','))
      } else {
        throw new WebCryptoError
      }
    } catch {
      return window.btoa(string)
    }
  }

  public async decrypt(string: string): Promise<string> {
    try {
      const { buffer } = new Uint8Array(window.atob(string).split(',').map(num => +num))
      const key = this[props.key as never]
      const encryptBy = { name: CRYPT_TYPE.AES_GCM, iv: this[props.secret as never] }

      if (key && subtle) {
        const decrypted = await subtle.decrypt(
          encryptBy,
          key,
          buffer
        )
        return decode(decrypted)
      } else {
        throw new WebCryptoError
      }
    } catch {
      return window.atob(string)
    }
  }

  public async validate() {
    if (this.isEmpty) return false

    try {
      await this.setup()
      await this.read()
      return true
    } catch (error) {
      return false
    }
  }

  public async read<T>(): Promise<T> {
    if (!this.storage.length) throw new WebCryptoStorageError('Empty storage.')

    await wrap(this.transaction)

    try {
      const key = WebCryptoStorage.SDK_CRYPTO_STORAGE_KEY
      const base64: string = String(this.storage.getItem(key)).trim() || SPACE

      if (!base64) throw new WebCryptoStorageError('Invalid or empty data.')

      const content = await this.decrypt(base64)

      return JSON.parse(content) || {}
    } catch (error) {
      throw new WebCryptoStorageError(`Unable to read data. ${error.message}`)
    }
  }

  public async save<T>(value: T): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const self = this as any
    if (this.transaction) {
      try {
        await this.transaction
      } catch {
        self[props.transaction] = Promise.resolve()
      }
    }

    const key = WebCryptoStorage.SDK_CRYPTO_STORAGE_KEY

    self[props.transaction] = new Promise((resolve, reject) => {
      this
        .encrypt(JSON.stringify(value))
        .then((base64: string) => {
          this.storage.setItem(key, base64)
          resolve(undefined)
        }).catch(error => {
          reject(new WebCryptoStorageError(`Unable to save data. ${error.message}`))
        })
    })

    await this.transaction

    self[props.transaction] = Promise.resolve()
  }

  public has(key: string) {
    return !!(<Storage> this[props.storage as never]).getItem(key)
  }

  public clear(): void {
    this.storage.removeItem(WebCryptoStorage.SDK_CRYPTO_STORAGE_VERSION_KEY)
    this.storage.removeItem(WebCryptoStorage.SDK_CRYPTO_STORAGE_KEY)
  }
}

export function randomKey(length = 10, max = 256) {
  return Array.from({ length }, () => Math.floor(Math.random() * max))
}

export const CryptoStorage = new WebCryptoStorage(CRYPTOSTORAGE_SECRET_KEY, 1.2)
